import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import downloadQr from '../assets/download-qr.svg';
import { TESTFLIGHT_URL, playStoreUrl, trackStoreClick, Placement, Platform } from '../utils/appStoreLinks';

const LIGHT_PURPLE = '#A76FFF';

interface StoreLinkProps {
    placement: Placement;
    platform: Platform;
}

const badgeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: '#fff',
    backgroundColor: '#000',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '10px',
    padding: '10px 22px',
    minWidth: '220px',
    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
    transition: 'transform 0.2s ease, filter 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        filter: 'brightness(1.15)',
        textDecoration: 'none'
    }
};

const BadgeText = ({ small, large }: { small: string; large: string }) => (
    <Box sx={{ lineHeight: 1.2, textAlign: 'left' }}>
        <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.85, color: 'inherit' }}>
            {small}
        </Typography>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, mt: -0.25, color: 'inherit' }}>
            {large}
        </Typography>
    </Box>
);

const GooglePlayBadge: React.FC<StoreLinkProps> = ({ placement, platform }) => (
    <Box
        component="a"
        href={playStoreUrl(placement)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackStoreClick('google_play', placement, platform)}
        sx={badgeSx}
    >
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 28, height: 28, mr: 1.5, flexShrink: 0 }}>
            <path fill="#4285F4" d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5Z"/>
            <path fill="#34A853" d="M16.81,15.12L6.05,21.34C5.79,21.5 5.50,21.57 5.21,21.57C4.60,21.57 4.03,21.26 3.84,21.85L13.69,12L16.81,15.12Z"/>
            <path fill="#FBBC05" d="M16.81,8.88L13.69,12L3.84,2.15C4.03,1.74 4.60,1.43 5.21,1.43C5.50,1.43 5.79,1.50 6.05,1.66L16.81,8.88Z"/>
            <path fill="#EA4335" d="M16.81,15.12C17.96,14.47 20.69,12.81 20.69,12.81C21.82,12.09 21.82,11.91 20.69,11.19L16.81,8.88L13.69,12L16.81,15.12Z"/>
        </Box>
        <BadgeText small="Get it on" large="Google Play" />
    </Box>
);

const IOSBetaBadge: React.FC<StoreLinkProps> = ({ placement, platform }) => (
    <Box
        component="a"
        href={TESTFLIGHT_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackStoreClick('ios_beta', placement, platform)}
        sx={badgeSx}
    >
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 28, height: 28, mr: 1.5, flexShrink: 0 }}>
            <path
                fill="white"
                d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z"
            />
        </Box>
        <BadgeText small="Join the" large="iOS beta" />
    </Box>
);

/**
 * The visitor's own store first: Android sees Google Play, iPhone sees the TestFlight beta
 * (with the other as a text link), and desktop sees both plus a QR code for their phone.
 */
export const StoreButtons: React.FC<StoreLinkProps & { onDark?: boolean; showQr?: boolean }> = ({
    placement,
    platform,
    onDark = false,
    showQr = false,
}) => {
    const noteSx = {
        fontSize: '0.9rem',
        color: onDark ? 'rgba(255,255,255,0.92)' : 'text.secondary',
        lineHeight: 1.5,
    };
    const linkSx = {
        color: onDark ? '#fff' : LIGHT_PURPLE,
        fontWeight: 600,
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
    };

    if (platform === 'android') {
        return (
            <Stack spacing={1.5} alignItems="inherit">
                <GooglePlayBadge placement={placement} platform={platform} />
                <Typography sx={noteSx}>
                    Free to download. On iPhone?{' '}
                    <Box
                        component="a"
                        href={TESTFLIGHT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackStoreClick('ios_beta', placement, platform)}
                        sx={linkSx}
                    >
                        Join the iOS beta
                    </Box>
                </Typography>
            </Stack>
        );
    }

    if (platform === 'ios') {
        return (
            <Stack spacing={1.5} alignItems="inherit">
                <IOSBetaBadge placement={placement} platform={platform} />
                <Typography sx={noteSx}>
                    Free beta through Apple's TestFlight app. On Android?{' '}
                    <Box
                        component="a"
                        href={playStoreUrl(placement)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackStoreClick('google_play', placement, platform)}
                        sx={linkSx}
                    >
                        Get it on Google Play
                    </Box>
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
            <Stack spacing={1.5}>
                <GooglePlayBadge placement={placement} platform={platform} />
                <IOSBetaBadge placement={placement} platform={platform} />
            </Stack>
            {showQr && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: '#fff',
                    borderRadius: 3,
                    p: 1.5,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                }}>
                    <Box
                        component="img"
                        src={downloadQr}
                        alt="QR code that opens this page on your phone"
                        width={112}
                        height={112}
                        sx={{ display: 'block' }}
                    />
                    <Typography sx={{ color: '#333', fontSize: '0.9rem', fontWeight: 600, maxWidth: 120, lineHeight: 1.4 }}>
                        Scan with your phone camera to get the app
                    </Typography>
                </Box>
            )}
        </Stack>
    );
};

export default StoreButtons;
